import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { FileText, Download, Loader2, CheckCircle, Lock as LockIcon } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { format, subDays, differenceInDays } from 'date-fns';
import { getCheckinDate } from '../lib/checkin-utils';
import { useSubscriptionPlan } from '../lib/use-subscription-plan';

interface PDFExportOptions {
  includeCoverPage?: boolean;
  includeTimeline?: boolean;
  includeCheckins?: boolean;
  includePatterns?: boolean;
  includeJournal?: boolean;
  dateRange?: 'all' | '7days' | '30days' | '90days';
}

export function PDFExport() {
  const [exporting, setExporting] = useState(false);
  const [options, setOptions] = useState<PDFExportOptions>({
    includeCoverPage: true,
    includeTimeline: true,
    includeCheckins: true,
    includePatterns: true,
    includeJournal: false,
    dateRange: '30days',
  });

  const { plan, features, loading: subLoading } = useSubscriptionPlan();
  const canExport = features.exportData;
  const isPro = plan === 'pro';

  if (!subLoading && !canExport) {
    return (
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <LockIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-neutral-900">PDF export is a paid feature</h3>
            <p className="text-sm text-neutral-600 mt-1">
              Upgrade to Basic to generate therapist-ready reports.
            </p>
            <Button
              onClick={() => {
                window.location.href = '/pricing';
              }}
              className="w-full mt-4 gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              Upgrade to unlock
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const generatePDF = async () => {
    setExporting(true);
    
    try {
      // Load data
      const relationships = JSON.parse(localStorage.getItem('tension_relationships') || '[]');
      const activeRelationshipId = localStorage.getItem('tension_active_relationship');
      const activeRelationship = relationships.find((r: any) => r.id === activeRelationshipId);
      const allCheckins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
      const journalEntries = JSON.parse(localStorage.getItem('tension_journal_entries') || '[]');

      // Filter check-ins for active relationship
      const checkins = allCheckins.filter((c: any) => c.relationshipId === activeRelationshipId);

      // Filter by date range
      let filteredCheckins = checkins;
      if (options.dateRange !== 'all') {
        const days = options.dateRange === '7days' ? 7 : options.dateRange === '30days' ? 30 : 90;
        const cutoffDate = subDays(new Date(), days);
        filteredCheckins = checkins.filter((c: any) => 
          new Date(c.timestamp) >= cutoffDate
        );
      }

      if (filteredCheckins.length === 0) {
        toast.error('No data available for the selected date range');
        setExporting(false);
        return;
      }

      // Initialize PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = margin;

      // Helper function to add new page if needed
      const checkPageBreak = (requiredSpace: number) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Helper function to draw horizontal line
      const drawLine = () => {
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
      };

      // ==================== COVER PAGE ====================
      if (options.includeCoverPage) {
        // Title
        doc.setFontSize(28);
        doc.setFont('helvetica', 'normal');
        doc.text('TENSION', pageWidth / 2, yPos, { align: 'center' });
        yPos += 10;

        doc.setFontSize(16);
        doc.setTextColor(100, 100, 100);
        doc.text('Relationship Tension Report', pageWidth / 2, yPos, { align: 'center' });
        yPos += 30;

        // Report Info
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text('Relationship:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(activeRelationship?.name || 'Unknown', margin + 40, yPos);
        yPos += 10;

        doc.setFont('helvetica', 'bold');
        doc.text('Type:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(activeRelationship?.type?.charAt(0).toUpperCase() + activeRelationship?.type?.slice(1) || 'Unknown', margin + 40, yPos);
        yPos += 10;

        doc.setFont('helvetica', 'bold');
        doc.text('Report Date:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(format(new Date(), 'MMMM d, yyyy'), margin + 40, yPos);
        yPos += 10;

        doc.setFont('helvetica', 'bold');
        doc.text('Date Range:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        const rangeText = options.dateRange === 'all' ? 'All time' : 
                         options.dateRange === '7days' ? 'Last 7 days' :
                         options.dateRange === '30days' ? 'Last 30 days' : 'Last 90 days';
        doc.text(rangeText, margin + 40, yPos);
        yPos += 10;

        doc.setFont('helvetica', 'bold');
        doc.text('Total Check-ins:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(filteredCheckins.length.toString(), margin + 40, yPos);
        yPos += 30;

        // Disclaimer
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        const disclaimer = 'This report is generated from self-reported behavioral observations and is intended as a supplementary tool for therapeutic discussion. It should not replace professional clinical assessment.';
        const splitDisclaimer = doc.splitTextToSize(disclaimer, pageWidth - 2 * margin);
        doc.text(splitDisclaimer, margin, yPos);
        yPos += splitDisclaimer.length * 5 + 20;

        // Privacy notice
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text('Privacy: This report contains sensitive personal information. Handle with care.', pageWidth / 2, pageHeight - 20, { align: 'center' });

        // New page for content
        doc.addPage();
        yPos = margin;
      }

      // ==================== EXECUTIVE SUMMARY ====================
      doc.setFontSize(18);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('Executive Summary', margin, yPos);
      yPos += 10;
      drawLine();
      yPos += 5;

      // Calculate metrics
      const latestCheckin = filteredCheckins[filteredCheckins.length - 1];
      const averageRTI = Math.round(
        filteredCheckins.reduce((sum: number, c: any) => sum + (c.rtiScore || 0), 0) / filteredCheckins.length
      );
      const currentRTI = latestCheckin?.rtiScore || 0;
      const currentState = currentRTI <= 35 ? 'Stable' : currentRTI <= 60 ? 'Elastic' : 'High Tension';
      
      // RTI trend
      const firstHalf = filteredCheckins.slice(0, Math.floor(filteredCheckins.length / 2));
      const secondHalf = filteredCheckins.slice(Math.floor(filteredCheckins.length / 2));
      const firstHalfAvg = firstHalf.length > 0 ? 
        Math.round(firstHalf.reduce((sum: number, c: any) => sum + (c.rtiScore || 0), 0) / firstHalf.length) : 0;
      const secondHalfAvg = secondHalf.length > 0 ?
        Math.round(secondHalf.reduce((sum: number, c: any) => sum + (c.rtiScore || 0), 0) / secondHalf.length) : 0;
      const trend = secondHalfAvg > firstHalfAvg ? 'Increasing' : 
                   secondHalfAvg < firstHalfAvg ? 'Decreasing' : 'Stable';

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');

      // Current RTI
      doc.setFont('helvetica', 'bold');
      doc.text('Current RTI Score:', margin, yPos);
      doc.setFont('helvetica', 'normal');
      const rtiColor = currentRTI <= 35 ? [34, 197, 94] : currentRTI <= 60 ? [234, 179, 8] : [239, 68, 68];
      doc.setTextColor(rtiColor[0], rtiColor[1], rtiColor[2]);
      doc.text(`${currentRTI} (${currentState})`, margin + 60, yPos);
      doc.setTextColor(0, 0, 0);
      yPos += 8;

      // Average RTI
      doc.setFont('helvetica', 'bold');
      doc.text('Average RTI:', margin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(averageRTI.toString(), margin + 60, yPos);
      yPos += 8;

      // Trend
      doc.setFont('helvetica', 'bold');
      doc.text('Trend:', margin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(trend, margin + 60, yPos);
      yPos += 8;

      // Check-in frequency
      const daysSinceFirst = differenceInDays(new Date(), new Date(getCheckinDate(filteredCheckins[0])));
      const frequency = daysSinceFirst > 0 ? (filteredCheckins.length / daysSinceFirst).toFixed(1) : 'N/A';
      doc.setFont('helvetica', 'bold');
      doc.text('Check-in Frequency:', margin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(`${frequency} per day`, margin + 60, yPos);
      yPos += 15;

      // Key observations
      doc.setFont('helvetica', 'bold');
      doc.text('Key Observations:', margin, yPos);
      yPos += 8;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);

      const observations = [];
      
      // Component analysis
      const avgComponents = {
        initiation: 0,
        latency: 0,
        emotionalDensity: 0,
        availability: 0,
        withdrawal: 0,
      };

      filteredCheckins.forEach((c: any) => {
        if (c.components) {
          avgComponents.initiation += c.components.initiation || 0;
          avgComponents.latency += c.components.latency || 0;
          avgComponents.emotionalDensity += c.components.emotionalDensity || 0;
          avgComponents.availability += c.components.availability || 0;
          avgComponents.withdrawal += c.components.withdrawal || 0;
        }
      });

      Object.keys(avgComponents).forEach((key) => {
        avgComponents[key as keyof typeof avgComponents] = Math.round(
          avgComponents[key as keyof typeof avgComponents] / filteredCheckins.length
        );
      });

      // Find highest component
      const componentNames = {
        initiation: 'Initiation Imbalance',
        latency: 'Response Latency',
        emotionalDensity: 'Emotional Density Drop',
        availability: 'Over-Availability',
        withdrawal: 'Withdrawal Signals',
      };

      const highestComponent = Object.entries(avgComponents).reduce((a, b) => a[1] > b[1] ? a : b);
      if (highestComponent[1] > 60) {
        observations.push(`• Primary tension driver: ${componentNames[highestComponent[0] as keyof typeof componentNames]} (${highestComponent[1]})`);
      }

      if (trend === 'Increasing') {
        observations.push(`• RTI has increased from ${firstHalfAvg} to ${secondHalfAvg} over the reporting period`);
      } else if (trend === 'Decreasing') {
        observations.push(`• Positive improvement: RTI decreased from ${firstHalfAvg} to ${secondHalfAvg}`);
      }

      if (currentState === 'High Tension') {
        observations.push('• Current state indicates need for restraint and space');
      }

      observations.forEach((obs) => {
        const lines = doc.splitTextToSize(obs, pageWidth - 2 * margin - 5);
        checkPageBreak(lines.length * 5);
        doc.text(lines, margin + 5, yPos);
        yPos += lines.length * 5;
      });

      yPos += 10;

      // ==================== RTI TIMELINE ====================
      if (options.includeTimeline) {
        checkPageBreak(80);
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('RTI Timeline', margin, yPos);
        yPos += 10;
        drawLine();
        yPos += 10;

        // Simple text-based timeline
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        
        // Table header
        doc.setFont('helvetica', 'bold');
        doc.text('Date', margin, yPos);
        doc.text('RTI', margin + 60, yPos);
        doc.text('State', margin + 85, yPos);
        doc.text('Change', margin + 120, yPos);
        yPos += 6;
        drawLine();
        
        doc.setFont('helvetica', 'normal');
        
        // Show last 20 check-ins or all if fewer
        const timelineCheckins = filteredCheckins.slice(-20);
        
        timelineCheckins.forEach((checkin: any, index: number) => {
          checkPageBreak(6);
          
          const date = format(new Date(getCheckinDate(checkin)), 'MMM d, yyyy');
          const score = checkin.rtiScore || 0;
          const state = score <= 35 ? 'Stable' : score <= 60 ? 'Elastic' : 'High';
          
          let change = '';
          if (index > 0) {
            const prevScore = timelineCheckins[index - 1].rtiScore || 0;
            const diff = score - prevScore;
            change = diff > 0 ? `+${diff}` : diff < 0 ? `${diff}` : '0';
          }
          
          doc.text(date, margin, yPos);
          doc.text(score.toString(), margin + 60, yPos);
          
          // Color-code state
          const stateColor = score <= 35 ? [34, 197, 94] : score <= 60 ? [234, 179, 8] : [239, 68, 68];
          doc.setTextColor(stateColor[0], stateColor[1], stateColor[2]);
          doc.text(state, margin + 85, yPos);
          doc.setTextColor(0, 0, 0);
          
          if (change) {
            doc.text(change, margin + 120, yPos);
          }
          
          yPos += 6;
        });
        
        yPos += 10;
      }

      // ==================== COMPONENT BREAKDOWN ====================
      if (options.includePatterns) {
        checkPageBreak(100);
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('RTI Component Analysis', margin, yPos);
        yPos += 10;
        drawLine();
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const intro = 'The RTI score is composed of five behavioral components. Below are the average values for each component over the reporting period:';
        const introLines = doc.splitTextToSize(intro, pageWidth - 2 * margin);
        doc.text(introLines, margin, yPos);
        yPos += introLines.length * 5 + 10;

        // Component bars
        const components = [
          { name: 'Initiation Imbalance', value: avgComponents.initiation, description: 'Frequency of contact initiation' },
          { name: 'Response Latency', value: avgComponents.latency, description: 'Changes in response time patterns' },
          { name: 'Emotional Density', value: avgComponents.emotionalDensity, description: 'Depth of emotional expression' },
          { name: 'Over-Availability', value: avgComponents.availability, description: 'Excessive availability signals' },
          { name: 'Withdrawal', value: avgComponents.withdrawal, description: 'Avoidance or distance patterns' },
        ];

        components.forEach((component) => {
          checkPageBreak(20);
          
          doc.setFont('helvetica', 'bold');
          doc.text(component.name, margin, yPos);
          yPos += 5;
          
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text(component.description, margin, yPos);
          yPos += 6;
          
          // Draw bar
          const barWidth = (pageWidth - 2 * margin - 30) * (component.value / 100);
          const barColor = component.value <= 35 ? [34, 197, 94] : component.value <= 60 ? [234, 179, 8] : [239, 68, 68];
          doc.setFillColor(barColor[0], barColor[1], barColor[2]);
          doc.rect(margin, yPos, barWidth, 4, 'F');
          
          // Draw background
          doc.setFillColor(230, 230, 230);
          doc.rect(margin + barWidth, yPos, (pageWidth - 2 * margin - 30) - barWidth, 4, 'F');
          
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(10);
          doc.text(component.value.toString(), pageWidth - margin - 20, yPos + 3);
          
          yPos += 10;
        });
        
        yPos += 10;
      }

      // ==================== RECENT CHECK-INS ====================
      if (options.includeCheckins) {
        checkPageBreak(60);
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Recent Check-Ins', margin, yPos);
        yPos += 10;
        drawLine();
        yPos += 10;

        // Show last 10 check-ins with details
        const recentCheckins = filteredCheckins.slice(-10).reverse();
        
        recentCheckins.forEach((checkin: any) => {
          checkPageBreak(30);
          
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          const dateStr = format(new Date(getCheckinDate(checkin)), 'EEEE, MMMM d, yyyy - h:mm a');
          doc.text(dateStr, margin, yPos);
          yPos += 7;
          
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.text(`RTI Score: ${checkin.rtiScore || 0}`, margin + 5, yPos);
          yPos += 6;
          
          if (checkin.emotionalAverage) {
            doc.text(`Emotional State: ${checkin.emotionalAverage}/100`, margin + 5, yPos);
            yPos += 6;
          }
          
          yPos += 5;
        });
      }

      // ==================== FOOTER ON EVERY PAGE ====================
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Generated by TENSION | ${format(new Date(), 'MMM d, yyyy')} | Page ${i} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      const fileName = `TENSION-Report-${activeRelationship?.name || 'Relationship'}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      doc.save(fileName);

      toast.success('PDF report generated successfully');
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
          <h3 className="font-medium text-neutral-900 dark:text-neutral-100">Export to PDF</h3>
        </div>

        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Generate a professional PDF report with your RTI data, perfect for sharing with therapists or counselors.
        </p>

        {/* Options */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="include-cover"
              checked={options.includeCoverPage}
              onChange={(e) => setOptions({ ...options, includeCoverPage: e.target.checked })}
              className="rounded border-neutral-300 dark:border-neutral-600"
            />
            <label htmlFor="include-cover" className="text-sm text-neutral-700 dark:text-neutral-300">
              Include cover page
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="include-timeline"
              checked={options.includeTimeline}
              onChange={(e) => setOptions({ ...options, includeTimeline: e.target.checked })}
              className="rounded border-neutral-300 dark:border-neutral-600"
            />
            <label htmlFor="include-timeline" className="text-sm text-neutral-700 dark:text-neutral-300">
              Include RTI timeline
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="include-checkins"
              checked={options.includeCheckins}
              onChange={(e) => setOptions({ ...options, includeCheckins: e.target.checked })}
              className="rounded border-neutral-300 dark:border-neutral-600"
            />
            <label htmlFor="include-checkins" className="text-sm text-neutral-700 dark:text-neutral-300">
              Include check-in details
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="include-patterns"
              checked={options.includePatterns}
              onChange={(e) => setOptions({ ...options, includePatterns: e.target.checked })}
              className="rounded border-neutral-300 dark:border-neutral-600"
            />
            <label htmlFor="include-patterns" className="text-sm text-neutral-700 dark:text-neutral-300">
              Include component analysis
            </label>
          </div>

          {isPro && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="include-journal"
                checked={options.includeJournal}
                onChange={(e) => setOptions({ ...options, includeJournal: e.target.checked })}
                className="rounded border-neutral-300 dark:border-neutral-600"
              />
              <label htmlFor="include-journal" className="text-sm text-neutral-700 dark:text-neutral-300">
                Include journal entries (PRO)
              </label>
            </div>
          )}
        </div>

        {/* Date Range */}
        <div className="pt-2">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 block mb-2">
            Date Range
          </label>
          <select
            value={options.dateRange}
            onChange={(e) => setOptions({ ...options, dateRange: e.target.value as any })}
            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
            <option value="90days">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>

        {/* Export Button */}
        <Button 
          onClick={generatePDF} 
          disabled={exporting}
          className="w-full gap-2"
        >
          {exporting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating PDF...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Generate PDF Report
            </>
          )}
        </Button>

        {/* Professional Note */}
        <div className="tension-glow bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-blue-900 dark:text-blue-100 font-medium mb-1">
                Professional Formatting
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                This PDF is formatted for professional use and can be shared directly with therapists, counselors, or relationship coaches.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
